import Profile from './Profile';
import NodePalette from './NodePalette';
import Toggle from '../Form/Toggle';
import { Button } from '../Form/Button';
import SidebarDrillDownItem from './SidebarDrillDownItem';
import CreateDrillDownItem from './CreateDrillDownItem';
import { getOS } from '../../utils/getOS';
import { Link } from 'react-router-dom';
import logo from '../../assets/dd-logo.png';
import { PencilIcon } from '@heroicons/react/16/solid';
import { openDiagramFormModal } from '../../features/dashboard/components/DiagramFormModal';
import SwimlanePalette from './SwimlanePalette';
import { useDiagramStore } from '../../features/diagram/ui/hooks/diagram-store.hook';
import { useCallback, useMemo } from 'react';
import FilterComboBox from '../../components/Form/FilterComboBox';

const Sidebar = () => {
  const data = useDiagramStore((state) => state.data);
  const rfNodes = useDiagramStore((state) => state.rfNodes);
  const rfEdges = useDiagramStore((state) => state.rfEdges);
  const updateSettings = useDiagramStore((state) => state.meta.updateSettings);
  const autoSpacing = useDiagramStore(
    (state) => state?.data?.settings?.autoSpacing
  );
  const snapToGrid = useDiagramStore(
    (state) => state?.data?.settings?.snapToGrid
  );

  const { nodes, edges } = useMemo(() => {
    const nodes = rfNodes.filter((n) => n.selected);
    const edges = rfEdges.filter((e) => e.selected);
    return { nodes, edges };
  }, [rfNodes, rfEdges]);

  const toggleSnapToGrid = useDiagramStore(
    (state) => state.meta.toggleSnapToGrid
  );

  const updateAutoSpacing = useCallback(() => {
    updateSettings({ autoSpacing: !autoSpacing });
  }, [autoSpacing, updateSettings]);

  const drilldowns = useDiagramStore((state) => state.drilldowns);
  const activeCategory = useDiagramStore((state) => state.activeCategory);
  const ddArray = useMemo(() => {
    if (!drilldowns) return [];
    return Object.values(drilldowns);
  }, [drilldowns]);

  const drillDownArray = useMemo(() => {
    if (!activeCategory) return ddArray;
    return ddArray.filter((drilldown) => {
      if (activeCategory) {
        return drilldown.categoryId === activeCategory;
      }
    });
  }, [ddArray, activeCategory]);

  const canEdit = true;
  // const drillDownFilter = useDiagramStore((state) => state.drillDownFilter);

  const showButton = useMemo(() => {
    return nodes.length > 0 || (edges.length > 0 && canEdit);
  }, [nodes, edges, canEdit]);

  const key = getOS() === 'MacOS' ? 'Cmd' : 'Ctrl';

  return (
    <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col border-r border-gray-200 bg-white px-6 overflow-y-scroll">
      <div className="flex h-16 shrink-0 items-center">
        <img src={logo} alt="" className="h-12" />
      </div>
      <nav className="flex flex-1 flex-col gap-y-3">
        <Link
          to="/v1/dashboard"
          className="text-xs font-semibold text-gray-400 hover:text-accent"
        >
          Back to Dashboard
        </Link>
        <div className="flex justify-between items-center">
          <h2>{data.name}</h2>
          {canEdit && (
            <Button
              type="button"
              variant="link"
              className="lg:inline-flex"
              onClick={() => openDiagramFormModal(data)}
            >
              <PencilIcon className="size-5 text-gray-500" />
            </Button>
          )}
        </div>
        {canEdit && (
          <>
            <Toggle
              label="Lock to Grid"
              enabled={snapToGrid}
              setEnabled={toggleSnapToGrid}
            />
            <Toggle
              label="Auto Spacing"
              enabled={autoSpacing}
              setEnabled={updateAutoSpacing}
            />
            <div>
              <h5 className="text-sm leading-8  text-gray-800 ">
                Node Palette
              </h5>
              <h6 className="text-xs font-semibold text-gray-600">
                Drag and drop to add nodes
              </h6>
            </div>
            <NodePalette />
            <div>
              <h5 className="text-sm leading-8  text-gray-800 ">Swimlanes</h5>
              <h6 className="text-xs font-semibold text-gray-600">
                Drag and drop to add swimlanes
              </h6>
            </div>
            <SwimlanePalette />
          </>
        )}
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            {ddArray.length ? (
              <div>
                <FilterComboBox />
              </div>
            ) : null}
            {ddArray.length && !drillDownArray.length ? (
              <div className="text-xs font-semibold text-gray-400 mt-2">
                No DrillDowns found for this category.
              </div>
            ) : !ddArray.length ? (
              <div className="text-xs font-semibold text-gray-400 mt-2">
                No DrillDowns created.
              </div>
            ) : null}
            <ul role="list" className="mt-2">
              {showButton && <CreateDrillDownItem />}
              {drillDownArray.length > 0 ? (
                <>
                  {drillDownArray.map((drillDown, idx) => (
                    <SidebarDrillDownItem
                      key={drillDown.id}
                      index={idx}
                      drillDown={drillDown} // Highlight drilldown on click
                    />
                  ))}
                </>
              ) : canEdit ? (
                <>
                  <p className="text-xs text-gray-400">
                    Please hold the{' '}
                    <kbd className="inline-flex items-center rounded border border-gray-200 px-1 font-sans text-xs text-gray-400">
                      {key}
                    </kbd>{' '}
                    key while clicking on nodes/edges to select them. You can
                    also hold the{' '}
                    <kbd className="inline-flex items-center rounded border border-gray-200 px-1 font-sans text-xs text-gray-400">
                      Shift
                    </kbd>{' '}
                    key to drag and select multiple nodes/edges at once.{' '}
                  </p>
                </>
              ) : (
                <p className="text-xs text-gray-400">
                  You do not have permission to create DrillDowns.
                </p>
              )}
            </ul>
          </li>
          <li className="-mx-6 mt-auto">
            <Profile />
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default Sidebar;
